export const KALEIDESCAPE_TOP_UP_STATUSES = [
  "pending_payment",
  "payment_received",
  "awaiting_wallet_credit",
  "completed",
  "cancelled",
] as const;

export type KaleidescapeTopUpStatus = (typeof KALEIDESCAPE_TOP_UP_STATUSES)[number];

export const KALEIDESCAPE_TOP_UP_PRESETS_USD = [100, 200, 500] as const;

export const KALEIDESCAPE_SETTINGS_KEYS = {
  usdSgdRate: "kaleidescape.usd_sgd_rate",
  adminFeePercent: "kaleidescape.admin_fee_percent",
  lowBalanceThreshold: "kaleidescape.low_balance_threshold",
  lowBalanceNotificationsEnabled: "kaleidescape.low_balance_notifications_enabled",
  paymentInstructions: "kaleidescape.payment_instructions",
  financeNotificationEmail: "kaleidescape.finance_notification_email",
  operatorNotificationEmail: "kaleidescape.operator_notification_email",
} as const;

export type KaleidescapePaymentInstructions = {
  payNowUen: string;
  payNowEntityName: string;
  instructionsText: string;
  bankTransferDetails?: string;
};

export const DEFAULT_KALEIDESCAPE_SETTINGS = {
  usdSgdRate: 1.36,
  adminFeePercent: 20,
  lowBalanceThresholdUsd: 20,
  lowBalanceNotificationsEnabled: true,
  financeNotificationEmail: "",
  operatorNotificationEmail: "",
  paymentInstructions: {
    payNowUen: "200208768C",
    payNowEntityName: "CLARITY AV PTE LTD",
    instructionsText:
      "Scan the PayNow QR code or transfer via PayNow using the UEN and reference shown. Please use the exact SGD amount indicated.",
    bankTransferDetails: "",
  } satisfies KaleidescapePaymentInstructions,
};

/** SGD Payable = USD × rate × (1 + adminFee/100) */
export function calculatePayableSgd(
  usdAmount: number,
  exchangeRate: number,
  adminFeePercent: number,
): number {
  if (!Number.isFinite(usdAmount) || usdAmount <= 0) return 0;
  return Math.round(usdAmount * exchangeRate * (1 + adminFeePercent / 100) * 100) / 100;
}

export function formatTopUpFormulaLine(
  usdAmount: number,
  exchangeRate: number,
  adminFeePercent: number,
  payableSgd: number,
): string {
  const multiplier = (1 + adminFeePercent / 100).toFixed(2);
  return `USD ${usdAmount.toFixed(2)} × ${exchangeRate.toFixed(2)} × ${multiplier} = SGD ${payableSgd.toFixed(2)}`;
}

/** Staff handoff copy: the requested USD remains the wallet-credit authority. */
export function formatKaleidescapeAdminHandoff(
  requestedUsdAmount: string | number,
  payableSgdAmount: string | number,
  paymentLabel = "Customer pays",
): string {
  const usd = Number(requestedUsdAmount);
  const sgd = Number(payableSgdAmount);
  return `${paymentLabel} S$${sgd.toFixed(2)} · Add to Kaleidescape account US$${usd.toFixed(2)}`;
}

export function topUpStatusLabel(status: KaleidescapeTopUpStatus): string {
  switch (status) {
    case "pending_payment":
      return "Pending Payment";
    case "payment_received":
      return "Payment Received";
    case "awaiting_wallet_credit":
      return "Awaiting Wallet Credit";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

/** Customer-facing status message shown in wallet history and confirmation screens. */
export function topUpCustomerStatusMessage(status: KaleidescapeTopUpStatus): string | null {
  switch (status) {
    case "pending_payment":
      return "We have received your top-up request and it is currently processing.";
    case "payment_received":
    case "awaiting_wallet_credit":
      return "We have received your payment and are currently processing your Kaleidescape top-up.";
    case "completed":
      return "Your Kaleidescape Wallet has been credited.";
    default:
      return null;
  }
}

export function topUpPaymentReference(requestId: number): string {
  return `KE-TU-${requestId}`;
}
